/* External libs */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isEqual } from "lodash";
import {
  Anchor,
  Breadcrumbs,
  Button,
  Group,
  LoadingOverlay,
  Stack,
  Table,
  TextInput,
  Title,
} from "@mantine/core";
import { zodResolver } from "mantine-form-zod-resolver";

/* Types */
import { type BingoGame } from "bingo/types/bingo-game.model";

/* Hooks */
import { useDebug } from "common/debug/hooks/useDebug";

import {
  useAddBingoGameMutation,
  useUpdateBingoGameMutation,
} from "bingo/api/bingo-game.api";

/* Contexts */
import {
  BingoGameFormProvider,
  useBingoGameForm,
} from "bingo/contexts/bingo-game.context";

/* Schemas */
import { bingoGameSchema } from "./BingoGameSchema";

import Collapsable from "common/admin/ui/Collapsable";

// Utils
import { openWindow } from "common/utils/openWindow";
import { useListPlayersQuery } from "quiz/api/players.endpoints";
import { Player } from "quiz/types/player.model";
import QueryWrapper from "common/components/QueryWrapper";
import { useRemoveBingoGameMutation } from "bingo/api/bingo-game.api";
import { ADMIN_BINGO_PATH, ADMIN_PATH } from "_urls";
import { bingoGameService } from "services/services";

export function BingoGameAdminForm({
  bingoGame,
  editMode,
  bingoGameId,
}: {
  bingoGame?: BingoGame;
  editMode: boolean;
  bingoGameId?: string;
}) {
  const { isDebugMode, log } = useDebug();

  const navigate = useNavigate();
  const listPlayersQuery = useListPlayersQuery({ bingoGameId: bingoGameId! });

  const [addBingoGame, addBingoGameResult] = useAddBingoGameMutation();
  const [updateBingoGame, updateBingoGameResult] = useUpdateBingoGameMutation();
  const [deleteBingoGame, { isLoading: isDeleting }] =
    useRemoveBingoGameMutation();
  const [isLoading, setIsLoading] = useState(false);

  const [bingoGameState, setBingoGameState] = useState(bingoGame);

  void setIsLoading;
  void addBingoGame;

  const [eventId, setEventId] = useState("");

  const { isError: isErrorAddBingoGameResult, error: errorAddBingoGameResult } =
    addBingoGameResult;
  const {
    isError: isErrorUpdateBingoGameResult,
    error: errorUpdateBingoGameResult,
  } = updateBingoGameResult;
  console.log("bingoGameId", bingoGameId);
  useEffect(() => {
    if (isDebugMode) {
      if (isErrorAddBingoGameResult) {
        log(
          `errorAddBingoGameResult: ${JSON.stringify(errorAddBingoGameResult)}`,
        );
      }
      if (isErrorUpdateBingoGameResult) {
        log(
          `errorUpdateBingoGameResult: ${JSON.stringify(
            errorUpdateBingoGameResult,
          )}`,
        );
      }
    }
  }, [
    isDebugMode,
    log,
    isErrorAddBingoGameResult,
    isErrorUpdateBingoGameResult,
    errorAddBingoGameResult,
    errorUpdateBingoGameResult,
  ]);

  const form = useBingoGameForm({
    // initialValues: initialFormValues(),
    validate: zodResolver(bingoGameSchema),
  });

  const isDirty = useMemo(
    () => !isEqual(bingoGame, form.values),
    [bingoGame, form],
  );

  const handleDeleteGame = async () => {
    if (!bingoGame?.id) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this game?",
    );
    if (!confirmDelete) return;

    try {
      await deleteBingoGame(bingoGame.id).unwrap();
      navigate("../bingogames"); // or wherever your list is
    } catch (error) {
      console.error("Failed to delete game", error);
    }
  };

  // const updateBingoGameStatus = async (
  //   bingoGame: BingoGame,
  //   newStatus: "NOT_STARTED" | "IN_PROGRESS" | "CLOSED",
  // ) => {
  //   setIsLoading(true);
  //   try {
  //     const currentGame = await fetchAndUpdateBingoGameState(bingoGame.id);
  //     if (!currentGame) return;

  //     const updatedGame = {
  //       ...currentGame,
  //       status: newStatus,
  //       // Reset drawNumbersList if setting to NOT_STARTED
  //       ...(newStatus === "NOT_STARTED" && { drawNumbersList: [] }),
  //     };

  //     const response = await updateBingoGame(updatedGame).unwrap();
  //     await fetchAndUpdateBingoGameState(bingoGame.id);
  //     console.log("Bingo game status updated successfully:", response);
  //   } catch (error) {
  //     console.error("Failed to update bingo game status:", error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  //Bingo Draw add number to list

  const updateBingoGameStatus = async (
    newStatus: "NOT_STARTED" | "IN_PROGRESS" | "CLOSED",
  ) => {
    if (!bingoGameState) return;
    setIsLoading(true);

    try {
      const currentGame = await fetchAndUpdateBingoGameState(bingoGameState.id);
      if (!currentGame) return;

      const updatedGame = {
        ...currentGame,
        status: newStatus,
        // Reset drawNumbersList if setting to NOT_STARTED
        ...(newStatus === "NOT_STARTED" && { drawNumbersList: [] }),
      };

      const response = await updateBingoGame(updatedGame).unwrap();
      await fetchAndUpdateBingoGameState(bingoGameState.id);
      console.log("Bingo game status updated successfully:", response);
    } catch (error) {
      console.error("Failed to update bingo game status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // const addRandomDrawNumber = async (bingoGame: BingoGame) => {
  //   setIsLoading(true);
  //   try {
  //     const currentGame = await fetchAndUpdateBingoGameState(bingoGame.id);
  //     if (!currentGame) return;

  //     if (currentGame.winnerInfo) {
  //       await updateBingoGameStatus(currentGame, "CLOSED");
  //       return;
  //     }

  //     if (currentGame.drawNumbersList.length >= 20) return;

  //     const availableNumbers = Array.from(
  //       { length: 20 },
  //       (_, i) => i + 1,
  //     ).filter((num) => !currentGame.drawNumbersList.includes(num));

  //     if (availableNumbers.length === 0) return;

  //     const newNumber =
  //       availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
  //     const updatedGame = {
  //       ...currentGame,
  //       drawNumbersList: [...currentGame.drawNumbersList, newNumber],
  //     };

  //     await updateBingoGame(updatedGame).unwrap();
  //     await fetchAndUpdateBingoGameState(bingoGame.id);
  //   } catch (error) {
  //     console.error("Failed to add random number:", error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const addRandomDrawNumber = async () => {
    if (!bingoGameState) return;
    setIsLoading(true);

    try {
      const currentGame = await fetchAndUpdateBingoGameState(bingoGameState.id);
      if (!currentGame) return;

      if (currentGame.winnerInfo) {
        await updateBingoGameStatus("CLOSED");
        return;
      }

      if (currentGame.drawNumbersList.length >= 20) return;

      const availableNumbers = Array.from(
        { length: 20 },
        (_, i) => i + 1,
      ).filter((num) => !currentGame.drawNumbersList.includes(num));

      if (availableNumbers.length === 0) return;

      const newNumber =
        availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
      const updatedGame = {
        ...currentGame,
        drawNumbersList: [...currentGame.drawNumbersList, newNumber],
      };

      await updateBingoGame(updatedGame).unwrap();
      await fetchAndUpdateBingoGameState(bingoGameState.id);
    } catch (error) {
      console.error("Failed to add random number:", error);
    } finally {
      setIsLoading(false);
    }
  };

  //list suppress
  // console.log(addBingoGame);
  // console.log(setIsLoading);
  console.log("bingoGame.winnerInfo", bingoGame?.winnerInfo);

  const winnerString =
    "Winner: " +
    bingoGame?.winnerInfo?.firstName +
    " " +
    bingoGame?.winnerInfo?.lastName +
    " " +
    bingoGame?.winnerInfo?.email +
    " " +
    bingoGame?.winnerInfo?.phoneNumber;

  // const handleUpdateQRCode = async (bingoGame: BingoGame, eventId: string) => {
  //   if (!eventId) {
  //     alert("Please enter an Event ID");
  //     return;
  //   }
  //   setIsLoading(true);

  //   try {
  //     // Create an updated game object with the new QR code access value
  //     const updatedGame = { ...bingoGame, gridQRCodeAccess: eventId };

  //     const response = await updateBingoGame(updatedGame).unwrap();
  //     await fetchAndUpdateBingoGameState(bingoGame.id); // Add this refresh
  //     console.log("Grid QR Code Access updated successfully:", response);
  //     alert("Grid QR Code Access updated!");

  //     // Optionally reset eventId in the calling component
  //     // setEventId(""); // Only use this if `setEventId` is available in context
  //   } catch (error) {
  //     console.error("Failed to update QR Code:", error);
  //     alert("Failed to update QR Code");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleUpdateQRCode = async (eventId: string) => {
    if (!eventId) {
      alert("Please enter an Event ID");
      return;
    }
    if (!bingoGameState) return;

    setIsLoading(true);

    try {
      // Create an updated game object with the new QR code access value
      const updatedGame = {
        ...bingoGameState,
        gridQRCodeAccess: eventId,
      };

      const response = await updateBingoGame(updatedGame).unwrap();
      await fetchAndUpdateBingoGameState(bingoGameState.id);
      console.log("Grid QR Code Access updated successfully:", response);
      alert("Grid QR Code Access updated!");
    } catch (error) {
      console.error("Failed to update QR Code:", error);
      alert("Failed to update QR Code");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAndUpdateBingoGameState = async (bingoGameId: string) => {
    try {
      const latestGame = await bingoGameService.getBingoGame(bingoGameId);
      setBingoGameState(latestGame);
      return latestGame;
    } catch (error) {
      console.error("Failed to fetch latest bingo game state:", error);
      return null;
    }
  };

  useEffect(() => {
    setBingoGameState(bingoGame);
  }, [bingoGame]); // Add this dependency array

  useEffect(() => {
    if (bingoGameId) {
      setIsLoading(true);
      fetchAndUpdateBingoGameState(bingoGameId).finally(() =>
        setIsLoading(false),
      );
    }
  }, [bingoGameId]);

  return (
    <>
      {isLoading ? (
        <div className="h-100 w-100 d-flex align-items-center justify-content-center">
          <div className="spinner-border" role="status">
            <LoadingOverlay visible={true} overlayOpacity={0} />
          </div>
        </div>
      ) : null}
      <BingoGameFormProvider form={form}>
        <Stack>
          <div
            className="sticky top-0 z-50 pb-4"
            style={{ backgroundColor: "#E9ECEF" }}
          >
            <Title order={3} align={"center"} m={"xs"}>
              {bingoGame?.name || "New Bingo Game"}
            </Title>
            <Breadcrumbs separator="→">
              <Anchor component={Link} to={`/${ADMIN_PATH}`}>
                Admin
              </Anchor>

              <Anchor
                component={Link}
                to={`/${ADMIN_PATH}/${ADMIN_BINGO_PATH}`}
              >
                Bingo
              </Anchor>

              <Anchor
                component={Link}
                to={`/${ADMIN_PATH}/${ADMIN_BINGO_PATH}/bingogames`}
              >
                Bingo games
              </Anchor>
            </Breadcrumbs>
            {/* Open Game and Screencast */}
            <Group position="right">
              <span className="flex items-center gap-2">
                <strong>Open Screencast</strong>
                <Button
                  disabled={!bingoGame}
                  className="bg-button-admin bg-green-600 text-white disabled:opacity-75"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    openWindow(
                      `/bingoscreen/bingogame/${bingoGame?.id}`,
                      1540,
                      220,
                    );
                  }}
                >
                  Ouvrir
                </Button>
              </span>
            </Group>
          </div>
          <Group className="grid  grid-cols-2"></Group>
          {/* Game Management */}
          <Collapsable title="Game Management">
            <Group className="grid grid-rows-6">
              <Group className="grid grid-cols-3">
                <TextInput
                  label="Event ID"
                  value={eventId}
                  onChange={(e) => setEventId(e.currentTarget.value)}
                  placeholder="Enter event ID"
                  className="col-span-2"
                />
                <Button
                  onClick={() => handleUpdateQRCode(eventId)}
                  mt="lg"
                  className="bg-button-admin disabled:opacity-75"
                  disabled={!bingoGameState}
                >
                  Update QR Code
                </Button>
              </Group>
              <div>
                Linked to event:{" "}
                {bingoGame?.gridQRCodeAccess?.trim() ? (
                  bingoGame.gridQRCodeAccess
                ) : (
                  <span className="text-red-500">NO EVENT ID ASSIGNED</span>
                )}
              </div>

              <div>
                Drawn numbers :{" "}
                <strong>{bingoGameState?.drawNumbersList.join(" - ")} </strong>
                <div>
                  Total Draws : {bingoGameState?.drawNumbersList.length}/20
                </div>
              </div>
              <Group className="grid grid-cols-3">
                <Button
                  className="bg-button-admin disabled:opacity-75"
                  disabled={
                    bingoGameState?.status !== "NOT_STARTED" || !isDirty
                  }
                  onClick={() => updateBingoGameStatus("IN_PROGRESS")}
                >
                  Start
                </Button>

                <Button
                  className="bg-button-admin disabled:opacity-75"
                  disabled={
                    bingoGameState?.status !== "IN_PROGRESS" ||
                    (bingoGameState?.drawNumbersList?.length ?? 0) >= 20 ||
                    !!bingoGameState?.winnerInfo
                  }
                  onClick={addRandomDrawNumber}
                >
                  Next
                </Button>

                <Button
                  className="bg-button-admin disabled:opacity-75"
                  disabled={
                    bingoGameState?.status !== "IN_PROGRESS" ||
                    !!bingoGameState?.winnerInfo
                  }
                  onClick={() => {
                    if (bingoGameState?.status === "IN_PROGRESS") {
                      updateBingoGameStatus("CLOSED");
                    }
                  }}
                >
                  Stop
                </Button>
              </Group>

              <div>Game Status: {bingoGameState?.status}</div>
              <div>
                {!!bingoGame?.winnerInfo ? (
                  <div className="text-green-600">{winnerString}</div>
                ) : null}
              </div>
            </Group>
          </Collapsable>

          {/* Practicipants */}
          <Collapsable title="Practicipants">
            <QueryWrapper<Player[]> query={listPlayersQuery}>
              {(players) => (
                <div>
                  <Table striped highlightOnHover>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone Number</th>
                        <th>Email</th>
                        <th>Bingo Grid Numbers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {players.map((player: Player) => (
                        <tr key={player.id}>
                          <td>
                            {player.firstName} {player.lastName}
                          </td>
                          <td>{player.phoneNumber}</td>
                          <td>{player.email}</td>
                          <td>
                            {player.bingoGridNumbers?.length &&
                              player.bingoGridNumbers.join(", ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </QueryWrapper>
          </Collapsable>
        </Stack>
        <Group position="center" mt="lg">
          <Button
            style={{ backgroundColor: "red" }}
            onClick={handleDeleteGame}
            loading={isDeleting}
            disabled={!bingoGame?.id}
          >
            Delete Game
          </Button>
        </Group>
      </BingoGameFormProvider>
    </>
  );
}
